import React, { CSSProperties } from "react";
import { NodeApi } from "react-arborist";
import { Menu, MenuButton, MenuItem, MenuItems } from "@headlessui/react";
import { ChevronDown, ChevronRight, MoreHorizontal, ShoppingCart } from "lucide-react";
import clsx from "clsx";
import { useTranslation } from "react-i18next";
import { CategoryDataTbl } from "types/Document";

type Props = {
  node: NodeApi<CategoryDataTbl>;
  style: CSSProperties;
  isActive: boolean;
  /** Master on the company's documents page: may star, edit and add categories. */
  canManage: boolean;
  onSelect: () => void;
  onToggleOpen: () => void;
  onInfo: () => void;
  onAddToCart: (event: React.MouseEvent) => void;
  onToggleFavorite: (event: React.MouseEvent) => void;
  onEdit: () => void;
  onAddChild: () => void;
};

const tapTarget = "flex h-10 w-10 shrink-0 items-center justify-center";
const menuItemClass =
  "block w-full px-4 py-3 text-left text-sm text-gray-800 data-[focus]:bg-gray-50";

/**
 * A category row for narrow screens: the name gets all the free width and
 * truncates, the folder-to-cart button stays on the row, and the rarer actions
 * sit behind one "..." menu. The wide screen's row (tree-container.tsx) packs
 * five 20px icons after the name, which are neither tappable nor reachable once
 * a long name pushes them out of view.
 */
const CategoryRowTouch: React.FC<Props> = ({
  node,
  style,
  isActive,
  canManage,
  onSelect,
  onToggleOpen,
  onInfo,
  onAddToCart,
  onToggleFavorite,
  onEdit,
  onAddChild,
}) => {
  const { t } = useTranslation();
  const { name, count, isFavourite } = node.data;
  const hasChildren = (node.children?.length ?? 0) > 0;

  return (
    <div
      style={style}
      tabIndex={0}
      onClick={onSelect}
      className={clsx("node_txt flex items-center", isActive && "active-node")}
    >
      {hasChildren ? (
        <button
          type="button"
          className={tapTarget}
          aria-label={node.isOpen ? t("collapse") : t("expand")}
          onClick={(e) => {
            e.stopPropagation();
            onToggleOpen();
          }}
        >
          {node.isOpen ? (
            <ChevronDown className="h-5 w-5" aria-hidden="true" />
          ) : (
            <ChevronRight className="h-5 w-5" aria-hidden="true" />
          )}
        </button>
      ) : (
        <span className="w-10 shrink-0" />
      )}
      <span className="min-w-0 flex-1 truncate" title={name}>
        {name}
      </span>
      <span className="ml-1 shrink-0 text-sm text-gray-500">
        [{count <= 0 ? "x" : count}]
      </span>
      <button
        type="button"
        className={tapTarget}
        aria-label={t("document_cart_add_folder")}
        onClick={onAddToCart}
      >
        <ShoppingCart className="h-5 w-5" aria-hidden="true" />
      </button>
      <Menu>
        <MenuButton
          className={tapTarget}
          aria-label={t("invoice_inbox_more_actions")}
          onClick={(e: React.MouseEvent) => e.stopPropagation()}
        >
          <MoreHorizontal className="h-5 w-5" aria-hidden="true" />
        </MenuButton>
        {/* The menu is drawn in a portal, but React still bubbles its clicks to
            the row above, which would open the category. */}
        <MenuItems
          anchor="bottom end"
          modal={false}
          onClick={(e: React.MouseEvent) => e.stopPropagation()}
          className="z-50 w-56 rounded-lg border border-gray-200 bg-white py-1 shadow-lg outline-none [--anchor-gap:4px]"
        >
          <MenuItem>
            <button type="button" className={menuItemClass} onClick={onInfo}>
              {t("info")}
            </button>
          </MenuItem>
          {canManage && (
            <>
              <MenuItem>
                <button
                  type="button"
                  className={menuItemClass}
                  onClick={onToggleFavorite}
                >
                  {isFavourite
                    ? t("remove_from_favorites")
                    : t("add_to_favorites")}
                </button>
              </MenuItem>
              <MenuItem>
                <button type="button" className={menuItemClass} onClick={onEdit}>
                  {t("edit")}
                </button>
              </MenuItem>
              <MenuItem>
                <button
                  type="button"
                  className={menuItemClass}
                  onClick={onAddChild}
                >
                  {t("add")}
                </button>
              </MenuItem>
            </>
          )}
        </MenuItems>
      </Menu>
    </div>
  );
};

export default CategoryRowTouch;
